import React from "react";
import Button from "components/button";
import Icon from "components/icon";
interface ListHeaderProps {
  t: any;
  onDraft: () => void;
  onExportClick: () => void;
  onImport: () => void;
  onAddFleet: () => void;
  // Bulk action props
  totalSelected: number;
  isBulkOpen: boolean;
  setIsBulkOpen: (open: boolean) => void;
  isBulkDisabled?: boolean;
  bulkRef: React.RefObject<HTMLDivElement>;
  allowedBulkActions: string[];
  stagedBulkAction: string | null;
  setStagedBulkAction: (action: string | null) => void;
  onBulkStatusUpdate: (action: string) => void;
  getBulkActionLabel: (action: string | null) => string;
  draftCount?: number;
}

const ListHeader: React.FC<ListHeaderProps> = ({
  t,
  onDraft,
  onExportClick,
  onImport,
  onAddFleet,
  totalSelected,
  isBulkOpen,
  setIsBulkOpen,
  isBulkDisabled,
  bulkRef,
  allowedBulkActions,
  stagedBulkAction,
  setStagedBulkAction,
  onBulkStatusUpdate,
  getBulkActionLabel,
  draftCount = 0,
}) => {
  return (
    <div className="flex items-center justify-between h-[40px]">
      <div className="flex items-center gap-4">
        {totalSelected > 0 && (allowedBulkActions.length > 0 || isBulkDisabled) && (
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-left-2 duration-200">
            <div className="relative" ref={bulkRef}>
              <Button
                type="button"
                variant="outline"
                color="tertiary"
                size="medium"
                iconRight={<Icon name={isBulkOpen ? "chevron-up" : "chevron-down"} size={16} />}
                onClick={() => setIsBulkOpen(!isBulkOpen)}
                disabled={isBulkDisabled || allowedBulkActions.length === 0}
                data-testid="dropdown-bulk-action"
              >
                {getBulkActionLabel(stagedBulkAction)}
              </Button>

              {isBulkOpen && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] rounded-xl border border-border bg-white py-1 shadow-lg">
                  {allowedBulkActions.map((action) => (
                    <button
                      key={action}
                      type="button"
                      onClick={() => {
                        setStagedBulkAction(action);
                        setIsBulkOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-greyScale-90 transition-colors"
                    >
                      {action === "Inactive" ? t("bulk-action-deactivate") : 
                       action === "Available" ? t("bulk-action-activate") : 
                       action === "Maintenance" ? t("bulk-action-maintenance") :
                       action === "Delete" ? t("bulk-action-delete") :
                       action}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="button"
              variant="fill"
              color="secondary"
              size="medium"
              disabled={isBulkDisabled || !stagedBulkAction}
              onClick={() => stagedBulkAction && onBulkStatusUpdate(stagedBulkAction)}
              data-testid="btn-apply-data"
            >
              {t("btn-apply-data")}
            </Button>
          </div>
        )}

        <span className="text-sm font-medium text-text-secondary">
          {t("selected-count", { count: totalSelected })}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          iconLeft={<Icon name="draft" size={15} />}
          onClick={onDraft}
          className="relative"
          data-testid="btn-draft"
        >
          {t("btn-draft")}
          {draftCount > 0 && (
            <span className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700 shadow-sm ring-2 ring-white">
              {draftCount}
            </span>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          iconLeft={<Icon name="export" size={15} />}
          onClick={onExportClick}
          data-testid="btn-export-fleet"
        >
          {t("btn-export")}
        </Button>

        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          iconLeft={<Icon name="import" size={15} />}
          onClick={onImport}
          data-testid="btn-import"
        >
          {t("btn-import")}
        </Button>

        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          iconLeft={<Icon name="add" size={16} />}
          onClick={onAddFleet}
          data-testid="btn-navigate-create"
        >
          {t("btn-add-fleet")}
        </Button>
      </div>
    </div>
  );
};

export default ListHeader;
